import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { GridRowSelectionModel } from '@mui/x-data-grid'
import {
  PaginationResponse,
  OrderByDirection,
  SearchPossibleEventOwnersRequest,
  FetchEventsResponse,
  PublicEventType,
} from '../swagger'
import { events as eventsApi } from '../api/swagger'
import { SnackbarSeverity } from '../components/Context/SnackbarContext'
import { useAppSnackbar } from './useAppSnackbar'
import { useLoadingIds } from './useLoadingIds'
import useLoadingContext from './useLoadingContext'

interface PotentialOwner {
  fullName: string
  roleName: string
  actorKey: number
}

interface UseEventReassignmentProps {
  onReassignComplete?: (pagination: PaginationResponse) => void
  currentPage: number
  pageSize: number
  totalCount: number
  events: FetchEventsResponse[]
}

interface UseEventReassignmentReturn {
  selectedRows: GridRowSelectionModel
  selectedEventTypes: string[]
  isReassignModalOpen: boolean
  selectedOwner: PotentialOwner | null
  isReassigning: boolean
  ownerOptions: PotentialOwner[]
  isLoadingOwners: boolean
  possibleOwnerSearchInput: string
  handleSelectionChange: (selectedRowIds: GridRowSelectionModel) => void
  handleOpenReassignModal: () => void
  handleCloseReassignModal: () => void
  handleReassignOwners: () => Promise<void>
  setSelectedOwner: (owner: PotentialOwner | null) => void
  setOwnerSearchInput: (input: string) => void
}

export const useEventReassignment = ({
  onReassignComplete,
  currentPage,
  pageSize,
  totalCount,
  events,
}: UseEventReassignmentProps): UseEventReassignmentReturn => {
  const { t } = useTranslation()
  const {
    handleError,
    setSnackbarMessage,
    setSnackbarState,
    setSnackbarSeverity,
  } = useAppSnackbar()

  // State
  const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([])
  const [selectedEventTypes, setSelectedEventTypes] = useState<string[]>([])
  const [isReassignModalOpen, setIsReassignModalOpen] = useState(false)
  const [selectedOwner, setSelectedOwner] = useState<PotentialOwner | null>(
    null
  )
  const [ownerOptions, setOwnerOptions] = useState<PotentialOwner[]>([])
  const [ownersSearchInput, setOwnersSearchInput] = useState('')

  // Track previous values to detect changes
  const [prevCurrentPage, setPrevCurrentPage] = useState(currentPage)
  const [prevPageSize, setPrevPageSize] = useState(pageSize)

  const {
    Events: { searchPossibleEventOwners, bulkReassignEvents },
  } = useLoadingIds()

  // Clear selections when page or page size changes
  useEffect(() => {
    const pageChanged = currentPage !== prevCurrentPage
    const pageSizeChanged = pageSize !== prevPageSize

    if (pageChanged || pageSizeChanged) {
      setSelectedRows([])

      // Update previous values
      setPrevCurrentPage(currentPage)
      setPrevPageSize(pageSize)
    }
  }, [currentPage, pageSize, prevCurrentPage, prevPageSize])

  // Update event types whenever selection changes
  useEffect(() => {
    if (!selectedRows.length || !events.length) {
      setSelectedEventTypes([])
      return
    }

    // selectedRowIds now contains event keys directly
    const selectedEventKeys = selectedRows.map((id) => Number(id))
    const selectedEvents = events.filter((event) =>
      selectedEventKeys.includes(event.eventKey)
    )

    const uniqueEventTypes = [
      ...new Set(selectedEvents.map((event) => event.eventType)),
    ]
    setSelectedEventTypes(uniqueEventTypes)
  }, [selectedRows, events])

  // Fetch possible event owners function
  const fetchPossibleEventOwnersFunction = useCallback(async () => {
    try {
      const params: SearchPossibleEventOwnersRequest = {
        orderBy: ['lastName ASC'],
        eventTypes: selectedEventTypes as PublicEventType[],
      }

      // Only add search param if it's provided and has at least 3 characters
      if (ownersSearchInput && ownersSearchInput.length >= 3) {
        params.search = ownersSearchInput
      }

      const response = await eventsApi.searchPossibleEventOwners(params)

      const validPossibleOwners =
        (response.possibleOwners?.map((owner) => ({
          fullName: owner.fullName,
          roleName: owner.roleName,
          actorKey: owner.actorKey,
        })) as PotentialOwner[]) || []

      // Only update options if we have results or if we're clearing the search
      if (
        validPossibleOwners.length > 0 ||
        !ownersSearchInput ||
        ownersSearchInput === ''
      ) {
        setOwnerOptions(validPossibleOwners)
      }
    } catch (e) {
      await handleError(e)
    }
  }, [handleError, selectedEventTypes, ownersSearchInput])

  // Use useLoadingContext for fetching possible owners
  const {
    isLoading: isLoadingOwners,
    triggerFetch: triggerFetchPossibleOwners,
  } = useLoadingContext({
    asyncFunction: fetchPossibleEventOwnersFunction,
    loadingId: searchPossibleEventOwners,
  })

  // Event handlers
  const handleSelectionChange = (selectedRowIds: GridRowSelectionModel) => {
    // selectedRowIds now contains event keys directly since we set id: eventKey in the table
    setSelectedRows(selectedRowIds)
  }

  const handleOpenReassignModal = useCallback(() => {
    if (selectedRows.length === 0) {
      setSnackbarMessage(
        t(
          'Event.Reassign.NoSelection',
          'Please select at least one event to reassign.'
        )
      )
      setSnackbarSeverity(SnackbarSeverity.Warning)
      setSnackbarState(true)
      return
    }
    setIsReassignModalOpen(true)
  }, [
    selectedRows,
    setSnackbarMessage,
    setSnackbarSeverity,
    setSnackbarState,
    t,
  ])

  const handleCloseReassignModal = useCallback(() => {
    setIsReassignModalOpen(false)
    setSelectedOwner(null)
    setOwnersSearchInput('')
    setOwnerOptions([]) // Clear owner options when closing modal
  }, [])

  // Bulk reassign events function
  const bulkReassignEventsFunction = useCallback(async () => {
    if (!selectedOwner) {
      throw new Error(
        t('Event.Reassign.NoOwner', 'Please select an owner to reassign to.')
      )
    }

    const newEventOwnerKey = selectedOwner.actorKey

    if (!newEventOwnerKey) {
      throw new Error(
        t(
          'Event.Reassign.Error.NoOwnerRole',
          'The selected owner does not have the correct permissions.'
        )
      )
    }

    // selectedRows now contains event keys directly
    const eventKeysToReassign = selectedRows.map((id) => Number(id))

    await eventsApi.bulkReassignEvents({
      body: {
        newEventOwnerKey,
        eventKeysToReassign,
      },
    })

    // Success handling
    setSnackbarMessage(
      t(
        'Event.Reassign.Success',
        'Events successfully reassigned to the new owner.'
      )
    )
    setSnackbarSeverity(SnackbarSeverity.Success)
    setSnackbarState(true)

    // Reset state
    handleCloseReassignModal()
    setSelectedRows([])

    if (onReassignComplete) {
      onReassignComplete({
        page: currentPage - 1,
        pageSize: pageSize,
        totalCount: totalCount,
        orderBy: [{ startDate: OrderByDirection.Asc }],
      })
    }
  }, [
    selectedOwner,
    selectedRows,
    t,
    setSnackbarMessage,
    setSnackbarSeverity,
    setSnackbarState,
    onReassignComplete,
    currentPage,
    pageSize,
    totalCount,
    handleCloseReassignModal,
  ])

  // Use useLoadingContext for bulk reassign
  const { isLoading: isReassigning, triggerFetch: triggerBulkReassign } =
    useLoadingContext({
      asyncFunction: bulkReassignEventsFunction,
      loadingId: bulkReassignEvents,
      callback: () => {
        // Any additional cleanup can go here if needed
      },
    })

  const handleReassignOwners = useCallback(async () => {
    if (!selectedOwner) {
      setSnackbarMessage(
        t('Event.Reassign.NoOwner', 'Please select an owner to reassign to.')
      )
      setSnackbarSeverity(SnackbarSeverity.Warning)
      setSnackbarState(true)
      return
    }

    try {
      triggerBulkReassign()
    } catch (e) {
      await handleError(e)
    }
  }, [
    selectedOwner,
    triggerBulkReassign,
    handleError,
    t,
    setSnackbarMessage,
    setSnackbarSeverity,
    setSnackbarState,
  ])

  // Effect to trigger fetching owners when search input or event types change
  useEffect(() => {
    // Only trigger API call if we have event types selected AND the modal is open
    if (selectedEventTypes.length === 0 || !isReassignModalOpen) {
      return
    }

    if (ownersSearchInput.length >= 3) {
      triggerFetchPossibleOwners()
    } else if (ownersSearchInput.length === 0) {
      // Only fetch supervisors when event types are available AND there's no search input
      triggerFetchPossibleOwners()
    }
  }, [
    ownersSearchInput,
    selectedEventTypes,
    triggerFetchPossibleOwners,
    isReassignModalOpen,
  ])

  return {
    selectedRows,
    selectedEventTypes,
    isReassignModalOpen,
    selectedOwner,
    isReassigning,
    ownerOptions,
    isLoadingOwners,
    possibleOwnerSearchInput: ownersSearchInput,
    handleSelectionChange,
    handleOpenReassignModal,
    handleCloseReassignModal,
    handleReassignOwners,
    setSelectedOwner,
    setOwnerSearchInput: setOwnersSearchInput,
  }
}

export default useEventReassignment
